import { useCallback, useEffect, useRef } from 'react';
import type { ChatState, Message } from '../../../../lib/use-chat';
import { useVoiceCall } from '../../../../lib/use-voice-call';
import { API_BASE } from '../../../../lib/api-base';
import { recoverVoiceReply } from '../voice-recovery';
import { sendVoiceHandoff } from '../../../../lib/voice-handoff';

export function useVoiceBridge(chat: ChatState) {
  const { ensureConversation, appendMessage, reload } = chat;

  // Persist each completed voice turn into the conversation. The promise chain
  // keeps turns in spoken order — a fast exchange must not race itself into a
  // shuffled transcript.
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());
  const onTurnTranscript = useCallback(
    (speaker: 'user' | 'orion', text: string) => {
      persistQueueRef.current = persistQueueRef.current
        .then(async () => {
          const conversationId = await ensureConversation();
          if (!conversationId) return;
          const res = await fetch(`${API_BASE}/api/voice/transcript`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ conversationId, speaker, text }),
          });
          if (!res.ok) throw new Error(`transcript persist failed (${res.status})`);
          const data = (await res.json()) as { message?: Message };
          if (data.message) appendMessage(data.message);
        })
        .catch((err: unknown) => {
          console.warn('[voice] transcript persist:', err instanceof Error ? err.message : err);
        });
    },
    [ensureConversation, appendMessage],
  );

  // The voice model's one tool: hand a request to the full assistant loop in this
  // conversation and return its reply to read aloud. If the stream drops or ends
  // without a reply (proxy/gateway 502 on a long turn — the loop still finishes
  // and persists), recover the answer by polling rather than speaking a false snag.
  const resolveAsk = useCallback(
    async (request: string, onThinking: (step: string) => void): Promise<string> => {
      const conversationId = await ensureConversation();
      if (!conversationId) throw new Error('no conversation');
      const startedAt = Date.now();
      try {
        const res = await fetch(`${API_BASE}/api/voice/ask`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversationId, request }),
        });
        if (res.ok && res.body) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          let reply: string | null = null;
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const frames = buf.split('\n\n');
            buf = frames.pop() ?? '';
            for (const frame of frames) {
              const line = frame.split('\n').find((l) => l.startsWith('data:'));
              if (!line) continue;
              let ev: { type?: string; text?: string; reply?: string; error?: string };
              try {
                ev = JSON.parse(line.slice(5).trim());
              } catch {
                continue;
              }
              if (ev.type === 'thinking' && typeof ev.text === 'string') onThinking(ev.text);
              else if (ev.type === 'reply' && typeof ev.reply === 'string') reply = ev.reply;
              else if (ev.type === 'error') break;
            }
          }
          if (reply) return reply;
        }
      } catch {
        // Stream dropped — fall through to recovery.
      }
      const recovered = await recoverVoiceReply(conversationId, startedAt);
      if (recovered) return recovered;
      throw new Error('no reply');
    },
    [ensureConversation],
  );

  // The model's own fast tools. The harness decides what's reachable and hands back an outcome
  // already shaped for speech, so there is nothing to interpret here — a transport failure is the
  // only thing this layer turns into words of its own.
  const callVoiceTool = useCallback(
    async (tool: string, params: Record<string, unknown>) => {
      const conversationId = await ensureConversation();
      if (!conversationId) return { kind: 'failed', result: "I couldn't reach that just now." };
      const res = await fetch(`${API_BASE}/api/voice/tool-call`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId, tool, params }),
      });
      const payload = (await res.json().catch(() => null)) as { kind?: string; result?: unknown } | null;
      if (!payload?.kind) return { kind: 'failed', result: "I couldn't reach that just now." };
      return { kind: payload.kind, result: payload.result };
    },
    [ensureConversation],
  );

  // Hand the call off to text. The assistant writes the message, in this conversation, through the
  // same loop the chat uses — so what lands is a real message with real links, not a transcript.
  const sendToChat = useCallback(async () => {
    const conversationId = await ensureConversation();
    if (!conversationId) return false;
    const sent = await sendVoiceHandoff(conversationId);
    if (sent) void reload();
    return sent;
  }, [ensureConversation, reload]);

  const voice = useVoiceCall({
    onTurnTranscript,
    resolveAsk,
    callVoiceTool,
    resolveConversationId: ensureConversation,
    sendToChat,
  });

  // Load the realtime SDK while the user is still reading the chat, not while they are waiting on a
  // connect. It is a dynamic import inside the transport's `open()`, so on a freshly loaded page its
  // fetch + parse sat inside the connect: 4092ms in the socket phase on the first call after a
  // refresh against 431ms on a warm page. Idle-scheduled so it cannot compete with the chat's own
  // rendering, and after a delay so a user who never calls has already finished loading everything
  // that matters.
  useEffect(() => {
    const warm = () => {
      void import('../../../../lib/voice-gemini-transport')
        .then((m) => m.preWarmGeminiTransport())
        .catch(() => {});
    };
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    const timer = window.setTimeout(() => (idle ? idle(warm) : warm()), 2_000);
    return () => window.clearTimeout(timer);
  }, []);

  // When a voice call winds down, re-sync: ask_symphony turns are persisted but
  // don't all reach the live chat (the duplexer never pushes USER messages, and
  // is offline on localhost), so the transcript can be missing turns until a
  // refetch. A trailing sync catches a turn still persisting as the user hangs up.
  const prevVoiceStatusRef = useRef(voice.status);
  useEffect(() => {
    const prev = prevVoiceStatusRef.current;
    prevVoiceStatusRef.current = voice.status;
    const wasActive = prev === 'connecting' || prev === 'live';
    const nowDone = voice.status === 'ended' || voice.status === 'idle' || voice.status === 'error';
    if (wasActive && nowDone) {
      void reload();
      const t = setTimeout(() => void reload(), 1500);
      return () => clearTimeout(t);
    }
  }, [voice.status, reload]);

  return voice;
}
